/**
 * Responsibility: build the shapes an industrial site is made of.
 *
 * Two kinds of output, because a works contains two kinds of thing and they want different treatment:
 *
 * - **Solids** — cooling towers, chimneys, tanks, spheres. Returned as ring stacks, which the world has emitted
 *   since the rock columns: concentric polygons threaded along a path, with a line emitter, a segment count and a
 *   depth mask that all already exist. These are *masked*, because they are sheet metal and concrete and an
 *   unmasked one is a see-through cage of rings rather than a tower.
 * - **Frames** — pylons, conductors, sheds, pipe racks. Returned as segment lists for `emitShapeSegments`, and
 *   deliberately unmasked: they are open steelwork, so there is no surface for a mask to represent and masking one
 *   would make it occlude like a slab.
 *
 * ## The cooling tower is the point of the whole feature
 *
 * A hyperboloid is the one silhouette this world does not otherwise have, and it costs a radius profile. Nothing
 * else here is a curve that narrows and then flares again — a cone reads as a slag heap and a cylinder as a silo,
 * and it is specifically the waist that makes it unmistakable.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { INDUSTRIAL } from '../config.js';

/** A segment record in the shape `emitShapeSegments` consumes. */
function line([x1, y1, z1], [x2, y2, z2], t1, t2) {
  return { x1, y1, z1, x2, y2, z2, t1, t2: t2 ?? t1 };
}

/**
 * A ring of points around a vertical axis.
 *
 * @param {number} x - Centre.
 * @param {number} z
 * @param {number} y - Height of the ring.
 * @param {number} radius
 * @param {number} sides
 * @param {number} [spin] - Rotation in turns, so two adjacent towers are not vertex-aligned.
 */
function ring(x, z, y, radius, sides, spin = 0) {
  const points = [];

  for (let i = 0; i < sides; i += 1) {
    const angle = ((i + spin) / sides) * Math.PI * 2;
    points.push([x + Math.cos(angle) * radius, y, z + Math.sin(angle) * radius]);
  }

  return { points };
}

/**
 * A solid of revolution, from a radius profile.
 *
 * The one function behind every masked shape here: a cooling tower, a chimney, a tank and a sphere differ only in
 * what `radiusAt` returns.
 *
 * @param {object} shape
 * @param {(t: number) => number} radiusAt - Radius as a fraction of the base radius, for `t` from 0 at the bottom
 *   to 1 at the top.
 * @returns {{rings: Array<object>}} In the shape `emitRingStack` and `emitSolidMask` consume.
 */
function revolved({ x, z, groundY, radius, height, rings, sides, spin = 0 }, radiusAt) {
  const stack = [];

  for (let i = 0; i < rings; i += 1) {
    const t = i / (rings - 1);
    stack.push(ring(x, z, groundY + height * t, radius * radiusAt(t), sides, spin));
  }

  return { rings: stack };
}

/**
 * A cooling tower: the hyperboloid.
 *
 * Piecewise quadratic rather than a true hyperbola — one curve from the base in to the waist, another from the
 * waist back out to the rim. At this number of rings the two are indistinguishable, and the parameters mean
 * something you can picture: where the waist is, how narrow it gets, how far it flares back.
 *
 * @param {{x: number, z: number, groundY: number, spin?: number}} placement
 */
export function coolingTowerSolid(placement) {
  const waistAt = INDUSTRIAL.COOLING_WAIST_AT;
  const waist = INDUSTRIAL.COOLING_WAIST_RATIO;
  const top = INDUSTRIAL.COOLING_TOP_RATIO;

  return revolved(
    {
      ...placement,
      radius: INDUSTRIAL.COOLING_BASE_RADIUS,
      height: INDUSTRIAL.COOLING_HEIGHT,
      rings: INDUSTRIAL.COOLING_RINGS,
      sides: INDUSTRIAL.COOLING_SIDES,
    },
    (t) => {
      if (t <= waistAt) {
        // Falling from the full base radius to the waist, steepest at the bottom.
        const k = 1 - t / waistAt;
        return waist + (1 - waist) * k * k;
      }

      // Flaring back out above the waist, which is the half of the curve that does the work.
      const k = (t - waistAt) / (1 - waistAt);
      return waist + (top - waist) * k * k;
    },
  );
}

/**
 * A chimney: a tall stack, tapering steadily.
 *
 * The only thing on a site taller than a cooling tower, and slender enough that the two are never confused.
 */
export function chimneySolid(placement) {
  return revolved(
    {
      ...placement,
      radius: INDUSTRIAL.CHIMNEY_BASE_RADIUS,
      height: INDUSTRIAL.CHIMNEY_HEIGHT,
      rings: INDUSTRIAL.CHIMNEY_RINGS,
      sides: INDUSTRIAL.CHIMNEY_SIDES,
    },
    (t) => 1 + (INDUSTRIAL.CHIMNEY_TOP_RATIO - 1) * t,
  );
}

/**
 * A storage tank: a plain cylinder, wider than it is tall.
 *
 * Deliberately plain. A tank farm reads through *repetition at a regular spacing*, so an interesting tank would
 * work against it — the grid is the signal, not the object.
 *
 * @param {object} placement - Plus `radius`.
 */
export function tankSolid(placement) {
  return revolved(
    {
      ...placement,
      height: placement.radius * INDUSTRIAL.TANK_HEIGHT_RATIO,
      rings: INDUSTRIAL.TANK_RINGS,
      sides: INDUSTRIAL.TANK_SIDES,
    },
    () => 1,
  );
}

/**
 * A spherical pressure vessel, standing clear of the ground on a skirt.
 *
 * Nothing else in this world is a sphere, so it needs no supporting context to read as what it is. The lift
 * matters: sitting on the ground it looks like a dome, and held up it looks like a pressure vessel.
 */
export function sphereSolid(placement) {
  const radius = INDUSTRIAL.SPHERE_RADIUS;
  const lift = radius * INDUSTRIAL.SPHERE_LIFT;

  const rings = INDUSTRIAL.SPHERE_RINGS;

  const solid = revolved(
    {
      ...placement,
      groundY: placement.groundY + lift,
      radius,
      height: radius * 2,
      rings,
      sides: INDUSTRIAL.SPHERE_SIDES,
    },

    /*
      A circle in profile: widest at the equator, narrowing toward both poles.

      **Inset by half a ring at each end so the poles are never reached exactly.** A ring of radius zero collapses to
      a point, and a ring stack still emits one closed polygon per ring — so hitting the pole would write a dozen
      zero-length segments at each end of every sphere. The inset leaves a small flat cap instead, which real
      pressure vessels have anyway.
    */
    (t) => Math.sin(((0.5 + t * (rings - 1)) / rings) * Math.PI),
  );

  return { solid, lift, radius };
}

/**
 * The skirt holding a sphere up: four legs from the ground to its underside.
 *
 * Segments rather than a solid, because open steelwork under a tank is exactly what should not be masked.
 */
export function sphereSkirtSegments(placement) {
  const radius = INDUSTRIAL.SPHERE_RADIUS;
  const lift = radius * INDUSTRIAL.SPHERE_LIFT;
  const segments = [];

  for (let i = 0; i < 4; i += 1) {
    const angle = (i / 4) * Math.PI * 2;
    const foot = radius * 0.72;

    segments.push(
      line(
        [placement.x + Math.cos(angle) * foot, placement.groundY, placement.z + Math.sin(angle) * foot],
        [placement.x + Math.cos(angle) * foot * 0.6, placement.groundY + lift, placement.z + Math.sin(angle) * foot * 0.6],
        0,
        0.3,
      ),
    );
  }

  return segments;
}

/**
 * A long shed: a flat-roofed box with ribs across the roof.
 *
 * The ribs are the whole difference between this and a blank slab. A 150-unit box with four lines on it reads as a
 * roof; the same box bare reads as a missing texture.
 *
 * @param {{x: number, z: number, groundY: number, facing: number}} shed
 */
export function shedSegments(shed) {
  const alongX = Math.cos(shed.facing);
  const alongZ = Math.sin(shed.facing);

  const at = (along, across, y) => [
    shed.x + alongX * along - alongZ * across,
    y,
    shed.z + alongZ * along + alongX * across,
  ];

  const half = INDUSTRIAL.SHED_LENGTH / 2;
  const side = INDUSTRIAL.SHED_WIDTH / 2;
  const top = shed.groundY + INDUSTRIAL.SHED_HEIGHT;

  const corners = (y) => [at(-half, -side, y), at(half, -side, y), at(half, side, y), at(-half, side, y)];
  const low = corners(shed.groundY);
  const high = corners(top);

  const segments = [];

  for (let i = 0; i < 4; i += 1) {
    segments.push(line(low[i], low[(i + 1) % 4], 0));
    segments.push(line(high[i], high[(i + 1) % 4], 1));
    segments.push(line(low[i], high[i], 0, 1));
  }

  // Ribs across the roof, evenly spaced and strictly inside the ends so none lands on an edge already drawn.
  for (let i = 1; i <= INDUSTRIAL.SHED_RIBS; i += 1) {
    const along = -half + (INDUSTRIAL.SHED_LENGTH * i) / (INDUSTRIAL.SHED_RIBS + 1);
    segments.push(line(at(along, -side, top), at(along, side, top), 1));
  }

  return segments;
}

/**
 * A pipe rack: parallel runs carried on trestles, low to the ground.
 *
 * Horizontal where everything else on a site is vertical, which is what it is for — it ties the tall things
 * together and stops a tank farm reading as unrelated cylinders standing in a field.
 *
 * @param {{x: number, z: number, groundY: number, facing: number}} rack
 * @param {(x: number, z: number) => number} sampleGround
 */
export function pipeRackSegments(rack, sampleGround) {
  const alongX = Math.cos(rack.facing);
  const alongZ = Math.sin(rack.facing);

  const at = (along, across) => [rack.x + alongX * along - alongZ * across, rack.z + alongZ * along + alongX * across];

  const half = INDUSTRIAL.PIPE_RUN_LENGTH / 2;
  const lines = INDUSTRIAL.PIPE_LINES;
  const spacing = INDUSTRIAL.PIPE_SPACING;
  const trestles = INDUSTRIAL.PIPE_TRESTLES;

  const segments = [];

  /** A point on a run, at the pipe height above whatever ground is under it. */
  const onRun = (along, across) => {
    const [x, z] = at(along, across);
    return [x, sampleGround(x, z) + INDUSTRIAL.PIPE_HEIGHT, z];
  };

  // The runs themselves, drawn in trestle-to-trestle pieces so they follow the ground.
  for (let l = 0; l < lines; l += 1) {
    const across = (l - (lines - 1) / 2) * spacing;

    for (let i = 0; i < trestles - 1; i += 1) {
      const a = -half + (INDUSTRIAL.PIPE_RUN_LENGTH * i) / (trestles - 1);
      const b = -half + (INDUSTRIAL.PIPE_RUN_LENGTH * (i + 1)) / (trestles - 1);
      segments.push(line(onRun(a, across), onRun(b, across), 0.45));
    }
  }

  // Trestles: an upright at each end of a crossbeam, which is what holds the runs up.
  const outer = ((lines - 1) / 2) * spacing;

  for (let i = 0; i < trestles; i += 1) {
    const along = -half + (INDUSTRIAL.PIPE_RUN_LENGTH * i) / (trestles - 1);

    for (const across of [-outer, outer]) {
      const [x, z] = at(along, across);
      const groundY = sampleGround(x, z);
      segments.push(line([x, groundY, z], [x, groundY + INDUSTRIAL.PIPE_HEIGHT, z], 0, 0.45));
    }

    segments.push(line(onRun(along, -outer), onRun(along, outer), 0.45));
  }

  return segments;
}

/**
 * A substation: a fenced yard with a gantry across it, transformers under it, and a bank of insulators on top.
 *
 * **What stops a transmission line going nowhere.** A line that fades out mid-air reads as unfinished, so every line
 * in the biome runs substation to substation — and this is the thing at the end of it. The insulator bank is what
 * makes a fenced yard read as *electrical* rather than as a compound: nothing else in the world is a row of short
 * verticals standing on a beam.
 *
 * @param {{x: number, z: number, groundY: number, facing: number}} yard
 * @param {(x: number, z: number) => number} sampleGround
 */
export function substationSegments(yard, sampleGround) {
  const alongX = Math.cos(yard.facing);
  const alongZ = Math.sin(yard.facing);

  const at = (along, across, y) => [
    yard.x + alongX * along - alongZ * across,
    y,
    yard.z + alongZ * along + alongX * across,
  ];

  const half = INDUSTRIAL.SUBSTATION_SIZE / 2;
  const gantryY = yard.groundY + INDUSTRIAL.SUBSTATION_GANTRY_HEIGHT;
  const span = Math.max(1, gantryY + INDUSTRIAL.INSULATOR_HEIGHT - yard.groundY);
  const fraction = (y) => Math.min(1, Math.max(0, (y - yard.groundY) / span));

  const segments = [];

  /*
    The fence, drawn on the ground it actually crosses. It is the only thing here that establishes the yard as a
    bounded place rather than as equipment standing in a field.
  */
  const corners = [
    [-half, -half],
    [half, -half],
    [half, half],
    [-half, half],
  ];

  for (let i = 0; i < 4; i += 1) {
    const [a, b] = [corners[i], corners[(i + 1) % 4]];

    for (let step = 0; step < 4; step += 1) {
      const t0 = step / 4;
      const t1 = (step + 1) / 4;

      const p = (t) => {
        const along = a[0] + (b[0] - a[0]) * t;
        const across = a[1] + (b[1] - a[1]) * t;
        const [x, , z] = at(along, across, 0);
        return [x, sampleGround(x, z) + 2, z];
      };

      segments.push(line(p(t0), p(t1), 0));
    }
  }

  /*
    The gantry: two portal frames with a beam between them, spanning the yard. The conductors terminate on this, which
    is why its height is a named constant rather than derived — `conductorAnchors` has to be able to find it.
  */
  for (const across of [-half * 0.55, half * 0.55]) {
    for (const along of [-half * 0.6, half * 0.6]) {
      const [x, , z] = at(along, across, 0);
      segments.push(line([x, sampleGround(x, z), z], at(along, across, gantryY), 0, fraction(gantryY)));
    }

    segments.push(line(at(-half * 0.6, across, gantryY), at(half * 0.6, across, gantryY), fraction(gantryY)));
  }

  segments.push(line(at(0, -half * 0.55, gantryY), at(0, half * 0.55, gantryY), fraction(gantryY)));

  // Insulator stacks along the beam. Short verticals in a row, which nothing else in the world is.
  for (let i = 0; i < INDUSTRIAL.SUBSTATION_INSULATORS; i += 1) {
    const along = -half * 0.6 + (half * 1.2 * i) / (INDUSTRIAL.SUBSTATION_INSULATORS - 1);

    for (const across of [-half * 0.55, half * 0.55]) {
      segments.push(
        line(at(along, across, gantryY), at(along, across, gantryY + INDUSTRIAL.INSULATOR_HEIGHT), fraction(gantryY), 1),
      );
    }
  }

  // Transformers: squat boxes under the gantry, which is the mass that makes the yard look occupied.
  const width = INDUSTRIAL.TRANSFORMER_WIDTH / 2;
  const depth = INDUSTRIAL.TRANSFORMER_DEPTH / 2;

  for (let i = 0; i < INDUSTRIAL.SUBSTATION_TRANSFORMERS; i += 1) {
    const along = -half * 0.5 + (half * 1.0 * i) / Math.max(1, INDUSTRIAL.SUBSTATION_TRANSFORMERS - 1);
    const [cx, , cz] = at(along, 0, 0);
    const baseY = sampleGround(cx, cz);
    const topY = baseY + INDUSTRIAL.TRANSFORMER_HEIGHT;

    const box = (y) => [
      at(along - width, -depth, y),
      at(along + width, -depth, y),
      at(along + width, depth, y),
      at(along - width, depth, y),
    ];

    const low = box(baseY);
    const high = box(topY);

    for (let c = 0; c < 4; c += 1) {
      segments.push(line(low[c], low[(c + 1) % 4], fraction(baseY)));
      segments.push(line(high[c], high[(c + 1) % 4], fraction(topY)));
      segments.push(line(low[c], high[c], fraction(baseY), fraction(topY)));
    }
  }

  return segments;
}

/**
 * Where a substation's conductors terminate: the tips of its gantry beam.
 *
 * Shaped like `conductorAnchors` so a span can join a pylon to a substation without either end knowing which it is
 * attached to — which is what lets the line network treat the two interchangeably.
 *
 * @param {{x: number, z: number, groundY: number, facing: number}} yard
 * @returns {Array<Array<[number, number, number]>>} One pair per conductor level, all at the gantry.
 */
export function substationAnchors(yard) {
  const acrossX = -Math.sin(yard.facing);
  const acrossZ = Math.cos(yard.facing);

  const y = yard.groundY + INDUSTRIAL.SUBSTATION_GANTRY_HEIGHT + INDUSTRIAL.INSULATOR_HEIGHT;
  const reach = (INDUSTRIAL.SUBSTATION_SIZE / 2) * 0.55;

  return INDUSTRIAL.PYLON_ARM_LEVELS.map(() => [
    [yard.x + acrossX * -reach, y, yard.z + acrossZ * -reach],
    [yard.x + acrossX * reach, y, yard.z + acrossZ * reach],
  ]);
}

/**
 * Where a pylon's conductors hang, in world coordinates.
 *
 * Separated from the pylon's own geometry because the *spans between* pylons need these points, so both the tower
 * and the cables have to agree about them — deriving them twice is how a cable ends up floating beside an arm.
 *
 * @param {{x: number, z: number, groundY: number, facing: number}} pylon
 * @returns {Array<Array<[number, number, number]>>} One pair of points per crossarm level.
 */
export function conductorAnchors(pylon) {
  const acrossX = -Math.sin(pylon.facing);
  const acrossZ = Math.cos(pylon.facing);

  return INDUSTRIAL.PYLON_ARM_LEVELS.map((level) => {
    const y = pylon.groundY + INDUSTRIAL.PYLON_HEIGHT * level - INDUSTRIAL.PYLON_INSULATOR_DROP;
    const reach = INDUSTRIAL.PYLON_ARM_HALF;

    return [
      [pylon.x + acrossX * -reach, y, pylon.z + acrossZ * -reach],
      [pylon.x + acrossX * reach, y, pylon.z + acrossZ * reach],
    ];
  });
}

/**
 * A lattice pylon: a splayed base narrowing to a body, with crossarms and X-bracing.
 *
 * **The bracing is what makes it steel rather than a post.** Four legs alone read as a mast; the same four legs
 * with panels of crossed diagonals read as a transmission tower, and it is the cheapest possible way to say so.
 *
 * @param {{x: number, z: number, groundY: number, facing: number}} pylon
 */
export function pylonSegments(pylon) {
  const height = INDUSTRIAL.PYLON_HEIGHT;
  const waistAt = INDUSTRIAL.PYLON_WAIST_AT;

  const fraction = (y) => Math.min(1, Math.max(0, (y - pylon.groundY) / height));

  /** Half-width of the body at a height fraction: splayed below the waist, parallel above it. */
  const halfAt = (t) =>
    t >= waistAt
      ? INDUSTRIAL.PYLON_WAIST_HALF
      : INDUSTRIAL.PYLON_BASE_HALF +
        (INDUSTRIAL.PYLON_WAIST_HALF - INDUSTRIAL.PYLON_BASE_HALF) * (t / waistAt);

  const alongX = Math.cos(pylon.facing);
  const alongZ = Math.sin(pylon.facing);

  /** One of the four legs' positions at a height fraction. */
  const leg = (corner, t) => {
    const half = halfAt(t);
    const sx = corner === 0 || corner === 3 ? -1 : 1;
    const sz = corner < 2 ? -1 : 1;

    return [
      pylon.x + alongX * half * sx - alongZ * half * sz,
      pylon.groundY + height * t,
      pylon.z + alongZ * half * sx + alongX * half * sz,
    ];
  };

  const segments = [];
  const panels = INDUSTRIAL.PYLON_BRACE_PANELS;

  // Legs and bracing, panel by panel up the body.
  for (let p = 0; p < panels; p += 1) {
    const lower = p / panels;
    const upper = (p + 1) / panels;

    for (let corner = 0; corner < 4; corner += 1) {
      const from = leg(corner, lower);
      const to = leg(corner, upper);
      segments.push(line(from, to, fraction(from[1]), fraction(to[1])));

      /*
        An X across each face of each panel. Two diagonals per face is what reads as lattice; a single diagonal
        reads as a mistake, and more than two is invisible at any distance the tower is seen from.
      */
      const next = (corner + 1) % 4;
      segments.push(line(leg(corner, lower), leg(next, upper), fraction(from[1]), fraction(to[1])));
      segments.push(line(leg(next, lower), leg(corner, upper), fraction(from[1]), fraction(to[1])));
    }

    // A closing ring at the top of each panel, which ties the four legs together.
    for (let corner = 0; corner < 4; corner += 1) {
      segments.push(line(leg(corner, upper), leg((corner + 1) % 4, upper), fraction(leg(corner, upper)[1])));
    }
  }

  /*
    Crossarms, and the insulator hanging under each tip. The drop is small but it is what puts the conductor
    *below* the steel rather than touching it, which is the detail that makes a pylon look like a pylon.
  */
  const acrossX = -Math.sin(pylon.facing);
  const acrossZ = Math.cos(pylon.facing);

  for (const level of INDUSTRIAL.PYLON_ARM_LEVELS) {
    const y = pylon.groundY + height * level;
    const half = halfAt(level);
    const reach = INDUSTRIAL.PYLON_ARM_HALF;
    const t = fraction(y);

    for (const sign of [-1, 1]) {
      const inner = [pylon.x + acrossX * half * sign, y, pylon.z + acrossZ * half * sign];
      const tip = [pylon.x + acrossX * reach * sign, y, pylon.z + acrossZ * reach * sign];

      segments.push(line(inner, tip, t));
      segments.push(line(tip, [tip[0], y - INDUSTRIAL.PYLON_INSULATOR_DROP, tip[2]], t));
    }
  }

  return segments;
}

/**
 * The conductors of one span, sagging between two supports.
 *
 * A parabola rather than a true catenary: at this sag the two are indistinguishable, and a parabola needs no
 * hyperbolic functions. The same call the suspension bridge's main cable makes.
 *
 * Only the lower levels are strung — see `CONDUCTOR_LEVELS`. Three levels of cable would treble the segment count of
 * the longest thing in the biome for a difference nobody can see.
 *
 * **Takes anchor lists rather than supports**, so a span joins a pylon to a pylon or a pylon to a substation without
 * knowing which is which. That is what lets the line network treat the two interchangeably, and it is why
 * `substationAnchors` is shaped like `conductorAnchors`.
 *
 * @param {Array<Array<[number, number, number]>>} a - Anchors at one end, from `conductorAnchors` or `substationAnchors`.
 * @param {Array<Array<[number, number, number]>>} b - Anchors at the other.
 */
export function conductorSegments(a, b) {
  const start0 = a[0][0];
  const end0 = b[0][0];
  const span = Math.hypot(end0[0] - start0[0], end0[2] - start0[2]);
  const sag = span * INDUSTRIAL.CONDUCTOR_SAG_RATIO;
  const steps = INDUSTRIAL.CONDUCTOR_STEPS;

  const segments = [];
  const levels = Math.min(INDUSTRIAL.CONDUCTOR_LEVELS, a.length);

  for (let level = 0; level < levels; level += 1) {
    for (let side = 0; side < 2; side += 1) {
      const start = a[level][side];
      const end = b[level][side];

      /** A point along the span, pulled down by the sag. Zero at both ends, deepest in the middle. */
      const at = (t) => {
        const drop = sag * (1 - Math.pow(2 * t - 1, 2));
        return [
          start[0] + (end[0] - start[0]) * t,
          start[1] + (end[1] - start[1]) * t - drop,
          start[2] + (end[2] - start[2]) * t,
        ];
      };

      for (let i = 0; i < steps; i += 1) {
        segments.push(line(at(i / steps), at((i + 1) / steps), 0.85));
      }
    }
  }

  return segments;
}
